/* eslint-disable max-len */
/* eslint-disable import/no-unresolved */
/* eslint-disable class-methods-use-this */
import appConfig from 'app-config';
import moment from 'moment';
import WMICLoggingService from './WMICLoggingService';
import { APP_NAMES } from './StringConstants';
import WMICApplicationUtil from './WMICApplicationUtil';
import WMICFeatureFlagUtil from './WMICFeatureFlagUtil/WMICFeatureFlagUtil';
import { getProxiedUrl } from 'wmic-portals-url-js';

const APP_AMP_NAME_SHORT = 'Acct-Mgmt-Portal';
const APP_RQB_NAME_SHORT = 'renters-quote-buy';
const {
    exceptionLoggerConfig: exceptionLoggerConfigRaw,
    xhrLoggerConfig: xhrLoggerConfigRaw,
    logLoggerConfig: logLoggerConfigRaw
} = appConfig || {};
const featureFlags = WMICFeatureFlagUtil.getFeatureFlags();
const operations = {
    log: 'log',
    warn: 'warn',
    info: 'info',
    error: 'error',
    debug: 'debug'
};

const LOG_ENABLED_DEFAULTS = {
    global: false,
    warn: false,
    info: false,
    error: false,
    log: false,
    debug: false
};

const DEFAULT_THROTTLE_WINDOW = 30;
const DEFAULT_THROTTLE_MAX = 5;

const normalizeBoolean = (value) => value === true || value === 'true';

const normalizeLogEnabledConfig = (value) => {
    if (value && typeof value === 'object') {
        return {
            ...LOG_ENABLED_DEFAULTS,
            ...value
        };
    }

    if (normalizeBoolean(value)) {
        return {
            global: true,
            warn: true,
            info: true,
            error: true,
            log: true,
            debug: true
        };
    }

    return LOG_ENABLED_DEFAULTS;
};

const asFiniteNumber = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const exceptionLoggerConfig = {
    ...exceptionLoggerConfigRaw,
    enabled: normalizeBoolean(exceptionLoggerConfigRaw?.enabled),
    sendToPC: normalizeBoolean(exceptionLoggerConfigRaw?.sendToPC),
    windowInSeconds: asFiniteNumber(exceptionLoggerConfigRaw?.windowInSeconds, DEFAULT_THROTTLE_WINDOW),
    maxExceptionsPerWindow: asFiniteNumber(exceptionLoggerConfigRaw?.maxExceptionsPerWindow, DEFAULT_THROTTLE_MAX)
};

const xhrLoggerConfig = {
    ...xhrLoggerConfigRaw,
    enabled: normalizeBoolean(xhrLoggerConfigRaw?.enabled),
    sendToPC: normalizeBoolean(xhrLoggerConfigRaw?.sendToPC),
    windowInSeconds: asFiniteNumber(xhrLoggerConfigRaw?.windowInSeconds, DEFAULT_THROTTLE_WINDOW),
    maxExceptionsPerWindow: asFiniteNumber(xhrLoggerConfigRaw?.maxExceptionsPerWindow, DEFAULT_THROTTLE_MAX)
};

const logLoggerConfig = {
    ...logLoggerConfigRaw,
    enabled: normalizeLogEnabledConfig(logLoggerConfigRaw?.enabled),
    sendToPC: normalizeBoolean(logLoggerConfigRaw?.sendToPC)
};

/**
 * Recursively removes null and undefined values from an object or array
 * @param {*} obj - The object to clean
 * @returns {*} Cleaned object without null/undefined values
 */
const removeNullValues = (obj, seen = new WeakSet()) => {
    if (obj === null || obj === undefined) {
        return undefined;
    }
    
    if (Array.isArray(obj)) {
        return obj
            .map(item => removeNullValues(item, seen))
            .filter(item => item !== undefined && item !== null);
    }
    
    if (typeof obj === 'object' && obj !== null) {
        if (seen.has(obj)) {
            return undefined;
        }
        seen.add(obj);

        const cleaned = {};
        Object.keys(obj).forEach(key => {
            const value = removeNullValues(obj[key], seen);
            if (value !== undefined && value !== null) {
                cleaned[key] = value;
            }
        });
        return cleaned;
    }
    
    return obj;
};

/**
 * Safely stringify objects handling circular references
 * @param {*} obj - The object to stringify
 * @returns {string} JSON string or error message
 */
const safeStringify = (obj) => {
    const seen = new WeakSet();
    
    try {
        return JSON.stringify(obj, (key, value) => {
            if (value !== null && typeof value === 'object') {
                if (seen.has(value)) {
                    return '[Circular]';
                }
                seen.add(value);
            }
            return value;
        });
    } catch (error) {
        return `[Stringify Error: ${error.message}]`;
    }
};

const getFormattedDateTimeForLogging = (unixTimeMilliseconds) => {
    if (!unixTimeMilliseconds || typeof unixTimeMilliseconds !== 'number') {
        unixTimeMilliseconds = Date.now();
    }
    
    try {
        const momentInstance = moment(unixTimeMilliseconds);
        if (!momentInstance.isValid()) {
            return moment(Date.now()).format('YYYY-MM-DD HH:mm:ss.SSS Z');
        }
        return momentInstance.format('YYYY-MM-DD HH:mm:ss.SSS Z');
    } catch (error) {
        console.warn('Error formatting date for logging:', error);
        return new Date().toISOString();
    }
};

const buildLogPayload = (
    logLevel,
    category,
    message,
    stackTrace,
    pageName,
    url,
    unixTimeMilliseconds,
    authData,
    cause
) => {
    let userId;
    if (authData) {
        userId = authData.user_id;
    }

    let auxMessage = message;
    if (typeof message === 'object') {
        auxMessage = safeStringify(message);
    }

    let safeCause = cause;
    if (cause && typeof cause === 'object') {
        safeCause = safeStringify(cause);
    }

    const payload = {
        level: logLevel,
        logCategory: category,
        message: auxMessage,
        stackTrace: stackTrace ? { value: stackTrace } : undefined,
        cause: safeCause,
        userId: userId,
        pageName: pageName,
        pageUrl: url,
        ts: getFormattedDateTimeForLogging(unixTimeMilliseconds || Date.now()) 
    };

    return payload;
};

const buildXhrLogPayload = (
    message,
    serverResponse,
    errorUrl,
    errorMethod,
    unixTimeMilliseconds
) => {
    let auxMessage = message;
    if (typeof message === 'object') {
        auxMessage = safeStringify(message);
    }

    let safeServerResponse = serverResponse;
    if (serverResponse && typeof serverResponse === 'object') {
        safeServerResponse = safeStringify(serverResponse);
    }

    const payload = {
        level: 'error',
        logCategory: 'xhr',
        message: auxMessage,
        serverResponse: safeServerResponse,
        errorUrl: errorUrl,
        errorMethod: errorMethod,
        ts: getFormattedDateTimeForLogging(unixTimeMilliseconds || Date.now())
    };

    return payload;
};

const buildHecBody = (payload, source, unixTimeMilliseconds, host) => {
    return {
        event: payload,
        source: source,
        host: host,
        time: unixTimeMilliseconds / 1000
    };
};

const getFeatureAMPAvailability = (featureName, ldFlags) => {
    const response = WMICFeatureFlagUtil.queryAvailabilityAMP(ldFlags, featureName);
    return response;
};

const shouldThrottle = (throttle, windowInSeconds, maxExceptionsPerWindow) => {
    const currentDate = new Date();
    const currentSeconds = currentDate.getSeconds();
    let loggedExceptionsCount = 0;
    throttle.history[currentSeconds] = (currentSeconds in throttle.history)
        ? throttle.history[currentSeconds] + 1
        : 1;
    for (let i = (currentSeconds - windowInSeconds) + 1;
        i <= currentSeconds;
        // eslint-disable-next-line no-plusplus
        i++
    ) {
        
        if (i in throttle.history) {
            loggedExceptionsCount += throttle.history[i];
        }
    }

    return loggedExceptionsCount > maxExceptionsPerWindow;
};

class WMICRemoteLogger {
    static getWMICRemoteLogger(loggerName, pageName, location, ldFlags, authData) {
        return new WMICRemoteLogger(loggerName, pageName, location, ldFlags, authData);
    }

    constructor(loggerName, pageName, location, ldFlags, authData) {
        this.name = loggerName;
        let auxPageName = typeof pageName === 'string' ? pageName : '';
        if (auxPageName.startsWith('/')) {
            auxPageName = auxPageName.substring(1);
        }
        this.pageName = auxPageName;
        this.location = location;
        this.ldFlags = ldFlags;
        this.authData = authData;
        this.loggerAuthData = logLoggerConfig?.authHeader;
        this.throttle = {
            history: {}
        };
        const appName = WMICApplicationUtil.getAppName();
        switch (appName) {
            case APP_NAMES.CE:
                this.appShortName = APP_RQB_NAME_SHORT;
                break;
            case APP_NAMES.AMP:
                this.appShortName = APP_AMP_NAME_SHORT;
                break;
            default:
                this.appShortName = undefined;
        }
    }

    setLocation({pageName, location} = {}) {
        if (pageName !== undefined) {
            this.pageName = pageName;
        }
        this.location = location;
    }

    /**
     * Sends log payload to PC via JSONRPC
     * @param {Object} payload - The log payload to send
     * @param {Object} additionalHeaders - Additional headers for the request
     * @returns {Promise} Promise from the JSONRPC call
     */
    sendLogToPC(payload, additionalHeaders = {}) {
        return WMICLoggingService.logToPC(payload, additionalHeaders);
    }

    remotelyLogXHRException(rejection) {
        const currentUnixTimeMilliseconds = Date.now();

        const payload = buildXhrLogPayload(
            `${rejection?.status} ${rejection?.statusText}`,
            rejection?.data,
            rejection?.config?.url,
            rejection?.config?.data ? rejection.config.data.method : '',
            currentUnixTimeMilliseconds
        );

        console.error('[XHR]', payload);

        // Send to PC if enabled OR if Splunk auth is not configured (fallback)
        const isSplunkConfigured = xhrLoggerConfig.authHeader &&
                                    xhrLoggerConfig.authHeader.includes('Splunk') &&
                                    !xhrLoggerConfig.authHeader.includes('{{');

        if (xhrLoggerConfig.sendToPC || !isSplunkConfigured) {
            this.sendLogToPC(payload, {});
        }
    }

    responseError(rejection) {
        const needToThrottle = shouldThrottle(this.throttle, xhrLoggerConfig.windowInSeconds, xhrLoggerConfig.maxExceptionsPerWindow);
        if (xhrLoggerConfig.enabled) {
            if (!needToThrottle) {
                this.remotelyLogXHRException(rejection);
            } else {
                console.warn(`Too many exceptions in the last ${xhrLoggerConfig.windowInSeconds} seconds, skipping console logging`);
            }
        }
        return Promise.reject(rejection);
    }

    remotelyLogLog(message, logType, pageName, location) {
        const currentUnixTimeMilliseconds = Date.now();
        const pageUrl = location && typeof location === 'object'
            ? location.pathname
            : undefined;
        const payload = buildLogPayload(
            logType,
            'log',
            message?.message || message,
            message?.stack,
            pageName,
            pageUrl,
            currentUnixTimeMilliseconds,
            this.authData
        );

        const consoleFn = console[logType] || console.log;
        consoleFn(`[${this.name}]`, payload);

        // Send to PC if enabled OR if Splunk auth is not configured (fallback)
        const isSplunkConfigured = logLoggerConfig.authHeader &&
                                    logLoggerConfig.authHeader.includes('Splunk') &&
                                    !logLoggerConfig.authHeader.includes('{{');

        if (logLoggerConfig.sendToPC || !isSplunkConfigured) {
            this.sendLogToPC(payload, {});
        }
    }

    logOperation(message, operation) {
        if (logLoggerConfig.enabled && logLoggerConfig.enabled.global && logLoggerConfig.enabled[operation]) {
            this.remotelyLogLog(message, operation, this.pageName, this.location);
        }
    }

    warn(message) { this.logOperation(message, operations.warn); }

    error(message) { this.logOperation(message, operations.error); }

    info(message) { this.logOperation(message, operations.info); }

    log(message) { this.logOperation(message, operations.log); }

    debug(message) { this.logOperation(message, operations.debug); }

    remotelyLogException(exception, cause) {
        const currentUnixTimeMilliseconds = Date.now();
        const payload = buildLogPayload(
            'error',
            'exception',
            exception?.message || exception,
            exception?.stack,
            this.pageName,
            this.location?.pathname,
            currentUnixTimeMilliseconds,
            this.authData,
            cause
        );

        console.error(`[${this.name}]`, payload);

        // Send to PC if enabled OR if Splunk auth is not configured (fallback)
        const isSplunkConfigured = exceptionLoggerConfig.authHeader &&
                                    exceptionLoggerConfig.authHeader.includes('Splunk') &&
                                    !exceptionLoggerConfig.authHeader.includes('{{');

        if (exceptionLoggerConfig.sendToPC || !isSplunkConfigured) {
            this.sendLogToPC(payload, {});
        }
    }

    logException(exception, cause) {
        if (exceptionLoggerConfig.enabled) {
            if (!shouldThrottle(this.throttle, exceptionLoggerConfig.windowInSeconds, exceptionLoggerConfig.maxExceptionsPerWindow)) {
                this.remotelyLogException(exception, cause);
            } else {
                console.warn(`Too many exceptions in the last ${exceptionLoggerConfig.windowInSeconds} seconds, skipping console logging`);
            }
        }
    }
}

export default WMICRemoteLogger;